import { useMemo } from 'react';
import * as THREE from 'three';
import type { ImageProps } from '../../types';
import {
  cameraDirectionFromAbyssRotation,
  cameraDirectionFromQuaternion,
  cameraEulerFromAbyssRotation,
  cameraQuaternionFromAbyssRotation,
  directionArrowFromXY,
} from '../Util';
import { type NeighbouringImageProps } from './NeighbouringImage';
import { useImageMeanDistance } from './useImageMeanDistance';

const DEFAULT_MAX_NEIGHBOURING_IMAGES = 20;
const DEFAULT_MAX_NEIGHBOURING_IMAGE_ANGLE = 45;

export type UseNeighbouringImagesProps = {
  image?: ImageProps;
  showMaxNeighbouringImages?: number;
  maxNeighbouringImageAngle?: number;
  allImages?: ImageProps[];

  color?: number;
  hoveredColor?: number;
  opacity?: number;
  lineWidth?: number;
  hoveredLineWidth?: number;
  shouldShowImage?: boolean;
  shouldShowFrame?: boolean;
  imageOpacity?: number;
  defaultMeanDistance?: number;

  onClick: (image: NeighbouringImageProps) => void;
  onHover: (image: NeighbouringImageProps | undefined) => void;
};
type UseNeighbouringImagesResult =
  | {
      neighbouringImages: NeighbouringImageProps[];
      imagesByDirection: (NeighbouringImageProps | undefined)[];
    }
  | undefined;

export const useNeighbouringImages = ({
  image,
  showMaxNeighbouringImages = DEFAULT_MAX_NEIGHBOURING_IMAGES,
  maxNeighbouringImageAngle = DEFAULT_MAX_NEIGHBOURING_IMAGE_ANGLE,
  allImages,
  color = 0xaaaaaa,
  hoveredColor = 0xff5522,
  opacity = 0.5,
  lineWidth = 2,
  hoveredLineWidth = 4,
  shouldShowImage,
  shouldShowFrame,
  imageOpacity = 0.4,
  defaultMeanDistance,
  onClick,
  onHover,
}: UseNeighbouringImagesProps): UseNeighbouringImagesResult => {
  const isEnabled = showMaxNeighbouringImages > 0;
  const { meanDistance: meanDistance2 } = useImageMeanDistance({
    image: isEnabled ? image : undefined,
    defaultMeanDistance,
  });

  const meanDistance = meanDistance2 * 1;

  const result: UseNeighbouringImagesResult = useMemo(() => {
    if (!image) return undefined;
    if (!showMaxNeighbouringImages) return undefined;
    if (!allImages) return undefined;
    if (!meanDistance) return undefined;

    // const startTime = performance.now();

    const { position, rotation } = image;

    const quat = cameraQuaternionFromAbyssRotation(rotation);
    const direction = cameraDirectionFromQuaternion(quat);

    const center = new THREE.Vector3(...position);
    center.addScaledVector(direction, meanDistance);

    const center2 = new THREE.Vector3(...position);
    center2.addScaledVector(direction, 0.75 * meanDistance);

    const matrix4 = new THREE.Matrix4();
    const scale = new THREE.Vector3(1, 1, 1);
    matrix4.compose(center, quat, scale).invert();

    const plane = new THREE.Plane(direction, 0);
    plane.translate(center);

    const plane2 = new THREE.Plane(direction, 0);
    plane2.translate(center2);

    const maxAngle = (maxNeighbouringImageAngle / 180) * Math.PI;
    const pixelsToMeters = meanDistance / image.width;
    const distanceLimit = pixelsToMeters * image.width * 2;
    const allNeighbouringImages: NeighbouringImageProps[] = [];
    const intersectPoint = new THREE.Vector3();
    const intersectPoint2 = new THREE.Vector3();
    allImages.forEach((image0) => {
      if (image0.name === image.name) return;
      const position0 = new THREE.Vector3(...image0.position);
      const direction0 = cameraDirectionFromAbyssRotation(image0.rotation);
      const angle = direction0.angleTo(direction);
      if (Math.abs(angle) > maxAngle) {
        return;
      }
      const ray0 = new THREE.Ray(position0, direction0);
      const intersectPoint0 = ray0.intersectPlane(plane, intersectPoint);
      const intersectPoint20 = ray0.intersectPlane(plane2, intersectPoint2);
      const distanceToCenter = intersectPoint0?.distanceTo(center);
      const distanceToCenter2 = intersectPoint20?.distanceTo(center2);
      if (
        intersectPoint0 &&
        intersectPoint20 &&
        distanceToCenter &&
        distanceToCenter2 &&
        distanceToCenter < distanceLimit
      ) {
        const distance0 = position0.distanceTo(intersectPoint0);
        position0.addScaledVector(direction0, distance0);
        const rotation0 = cameraEulerFromAbyssRotation(image0.rotation);

        const pixelsToMeters0 = distance0 / image0.focalLength;

        intersectPoint20.applyMatrix4(matrix4);
        const directionArrow = directionArrowFromXY(intersectPoint20.x, intersectPoint20.y);

        allNeighbouringImages.push({
          image: image0,
          key: image0.url,
          position: [position0.x, position0.y, position0.z],
          rotation: [rotation0.x, rotation0.y, rotation0.z],
          scale: [image0.width * pixelsToMeters0, image0.height * pixelsToMeters0, 1],
          distance: distance0,
          color,
          hoveredColor,
          opacity,
          lineWidth,
          hoveredLineWidth,
          shouldShowImage,
          shouldShowFrame,
          imageOpacity,
          onClick,
          onHover,
          distanceToCenter,
          distanceToCenter2,
          directionArrow,
        });
      }
    });
    const neighbouringImages = allNeighbouringImages
      .sort(({ distanceToCenter2: d0 }, { distanceToCenter2: d1 }) => d0 - d1)
      .slice(0, showMaxNeighbouringImages);
    // const elapsedTime = performance.now() - startTime;
    // console.log('find neighbousing images time, msec', elapsedTime.toFixed(1));

    const imagesByDirection: (NeighbouringImageProps | undefined)[] = [
      undefined,
      undefined,
      undefined,
      undefined,
    ];
    neighbouringImages.forEach((image) => {
      if (!imagesByDirection[image.directionArrow]) {
        imagesByDirection[image.directionArrow] = image;
      }
    });

    return { neighbouringImages, imagesByDirection };
  }, [
    image,
    meanDistance,
    allImages,
    showMaxNeighbouringImages,
    maxNeighbouringImageAngle,
    color,
    hoveredColor,
    opacity,
    lineWidth,
    hoveredLineWidth,
    shouldShowImage,
    shouldShowFrame,
    imageOpacity,
    onClick,
    onHover,
  ]);

  return result;
};
